import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  PartyPopper, Truck, Users, Calendar, UtensilsCrossed,
  Wine, Shield, ChevronLeft, ChevronRight, Check, Sparkles,
  ArrowRight, MessageCircle, Package as PackageIcon, Minus, Plus, User, Phone, Download,
  MapPin, Crown, Award, Repeat, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  drinkItems as defaultDrinkItems, deliveryItems, eventTypes,
  teamRoles, structureItems, partners, STRUCTURE_FEE_PER_PERSON as DEFAULT_STRUCTURE_FEE,
  getPackagesForEvent as defaultGetPackagesForEvent,
  getPackageById as defaultGetPackageById,
  ALMOCO_JANTAR as DEFAULT_ALMOCO_JANTAR,
} from '../data/mockData';
import { usePricing } from '../hooks/usePricing';
import { generateBudgetPdf } from '../utils/generatePdf';
import { InlineDatePicker, formatDateBR } from './InlineDatePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '5531999172997';

// ---------- Visitor tracking ----------
const getVisitorId = () => {
  let vid = localStorage.getItem('buffet_visitor_id');
  if (!vid) {
    vid = 'v_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    localStorage.setItem('buffet_visitor_id', vid);
  }
  return vid;
};

const trackEvent = async (action, details = null) => {
  try {
    await axios.post(`${API}/visitors/interactions`, {
      visitor_id: getVisitorId(), action, details,
    });
  } catch (e) { /* silent */ }
};

const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
const fmtBRL2 = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EventSimulator = () => {
  // ---------- Pricing overrides (admin-editable) ----------
  const {
    drinkItems,
    structureFee: STRUCTURE_FEE_PER_PERSON,
    almocoJantar: ALMOCO_JANTAR,
    getPackagesForEvent,
    getPackageById,
  } = usePricing();

  // ---------- Step state ----------
  const [serviceType, setServiceType] = useState(null);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const contentRef = useRef(null);

  // ---------- Event data ----------
  const [guests, setGuests] = useState(100);
  const [eventType, setEventType] = useState(null);
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [locationToDefine, setLocationToDefine] = useState(false);

  // ---------- Package ----------
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [excludedItems, setExcludedItems] = useState(new Set()); // ids removidos do pacote
  const [substitutePratoQuente, setSubstitutePratoQuente] = useState(false);
  const [expandedPackageId, setExpandedPackageId] = useState(null); // expand inline para "Ver detalhes"

  // ---------- Drinks / partners / delivery ----------
  const [selectedDrinks, setSelectedDrinks] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [deliveryQuantities, setDeliveryQuantities] = useState({});
  const [blockedDates, setBlockedDates] = useState(new Set());

  // ---------- Lead capture ----------
  const [leadName, setLeadName] = useState('');
  const [leadWhatsapp, setLeadWhatsapp] = useState('');
  const [leadId, setLeadId] = useState(null);

  // Register visitor + load blocked dates on mount
  useEffect(() => {
    axios.post(`${API}/visitors`, { visitor_id: getVisitorId() }).catch(() => {});
    trackEvent('page_view');
  }, []);
  useEffect(() => {
    axios.get(`${API}/calendar`)
      .then((res) => {
        const blocked = new Set(
          (res.data || [])
            .filter((e) => ['bloqueado', 'fechado'].includes(e.status))
            .map((e) => e.date)
        );
        setBlockedDates(blocked);
      })
      .catch(() => {});
  }, []);

  // ---------- Intersection / visibility ----------
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollToContent = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // ---------- Step definitions ----------
  const steps = useMemo(() => {
    if (!serviceType) return ['Serviço'];
    if (serviceType === 'delivery') return ['Serviço', 'Itens', 'Contato', 'Proposta'];
    return ['Serviço', 'Dados', 'Pacote', 'Personalizar', 'Bebidas', 'Inclusos', 'Contato', 'Proposta'];
  }, [serviceType]);
  const totalSteps = steps.length;
  const progress = ((step + 1) / totalSteps) * 100;
  const isLeadStep = step === totalSteps - 2;
  const isResultStep = step === totalSteps - 1;

  // ---------- Selected package + derived items ----------
  const selectedPackage = useMemo(() => getPackageById(selectedPackageId), [selectedPackageId]);
  const availablePackages = useMemo(() => eventType ? getPackagesForEvent(eventType) : [], [eventType]);

  const activeItems = useMemo(() => {
    if (!selectedPackage) return [];
    return selectedPackage.items
      .filter((it) => !excludedItems.has(it.id))
      .map((it) => ({ ...it }));
  }, [selectedPackage, excludedItems]);

  const hasPratoQuente = useMemo(
    () => activeItems.some((it) => it.swappable),
    [activeItems]
  );

  // ---------- Price calculations ----------
  const calcPackageTotal = () => {
    if (!selectedPackage) return 0;
    return Math.round(selectedPackage.pricePerPerson * guests);
  };
  const calcSubstitutionAdjust = () => {
    if (!selectedPackage || !hasPratoQuente || !substitutePratoQuente) return 0;
    // Almoço/Jantar é tratado como acréscimo do valor de R$ 45/p
    // (o prato quente é parte do pacote, então cobramos a diferença completa do Almoço)
    return ALMOCO_JANTAR.pricePerPerson * guests;
  };
  const calcDrinksTotal = () => selectedDrinks.reduce((acc, id) => {
    const d = drinkItems.find((x) => x.id === id);
    return acc + (d ? d.pricePerPerson * guests : 0);
  }, 0);
  const calcStructureTotal = () => STRUCTURE_FEE_PER_PERSON * guests;
  const calcDeliveryTotal = () => Object.entries(deliveryQuantities).reduce((acc, [id, qty]) => {
    const d = deliveryItems.find((x) => x.id === id);
    return acc + (d ? d.pricePerUnit * qty : 0);
  }, 0);

  const totalPrice = serviceType === 'delivery'
    ? calcDeliveryTotal()
    : calcPackageTotal() + calcSubstitutionAdjust() + calcDrinksTotal() + calcStructureTotal();

  // ---------- Navigation ----------
  const canProceed = () => {
    if (step === 0) return !!serviceType;
    if (serviceType === 'delivery') {
      if (step === 1) return Object.values(deliveryQuantities).some((q) => q > 0);
      if (isLeadStep) return leadName.trim().length >= 2 && leadWhatsapp.trim().length >= 10;
      return true;
    }
    if (step === 1) {
      const guestsOk = guests >= 40;
      const typeOk = !!eventType;
      const dateOk = !!eventDate;
      const locationOk = locationToDefine || eventLocation.trim().length >= 3;
      return guestsOk && typeOk && dateOk && locationOk;
    }
    if (step === 2) return !!selectedPackageId;
    if (step === 3) return !!selectedPackageId; // personalizar é opcional
    if (step === 4) return true; // bebidas opcionais
    if (step === 5) return true; // resumo equipe/estrutura
    if (isLeadStep) return leadName.trim().length >= 2 && leadWhatsapp.trim().length >= 10;
    return true;
  };

  const createLead = async () => {
    try {
      const res = await axios.post(`${API}/leads`, {
        visitor_id: getVisitorId(),
        name: leadName,
        whatsapp: leadWhatsapp,
        service_type: serviceType,
        event_type: eventType,
        guests,
        event_date: eventDate,
        event_location: locationToDefine ? null : eventLocation,
        location_to_define: locationToDefine,
        package_id: selectedPackageId,
        package_name: selectedPackage?.name,
        package_tier: selectedPackage?.tier,
        excluded_items: Array.from(excludedItems),
        substitute_prato_quente: substitutePratoQuente,
        drinks: selectedDrinks,
        partners: selectedPartners,
        delivery_items: deliveryQuantities,
        total_price: totalPrice,
      });
      setLeadId(res.data.id);
      trackEvent('lead_created', { lead_id: res.data.id, total_price: totalPrice });
    } catch (e) { /* silent */ }
  };

  const goNext = () => {
    if (!canProceed()) return;
    const nextStep = step + 1;
    setDirection(1);
    if (nextStep === totalSteps - 1) { createLead(); }
    setStep(nextStep);
    setTimeout(scrollToContent, 50);
  };
  const goPrev = () => {
    if (step === 0) return;
    setDirection(-1);
    setStep(step - 1);
    setTimeout(scrollToContent, 50);
  };

  // ---------- Toggles ----------
  const toggleExcluded = (id) => {
    setExcludedItems((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleDrink = (id) => {
    setSelectedDrinks((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };
  const togglePartner = (id) => {
    setSelectedPartners((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };
  const updateDelivery = (id, delta) => {
    setDeliveryQuantities((p) => {
      const cur = p[id] || 0;
      const next = Math.max(0, cur + delta);
      return { ...p, [id]: next };
    });
  };

  // ---------- WhatsApp message + URL ----------
  const buildWhatsAppMessage = () => {
    let msg = `Olá! Acabei de gerar meu orçamento no site da Buffet MultiEventos.\n\n`;
    msg += `Nome: ${leadName}\n`;
    if (serviceType === 'presencial') {
      const evt = eventTypes.find((e) => e.id === eventType);
      msg += `Tipo: Evento Presencial\n`;
      msg += `Evento: ${evt?.label || ''}\n`;
      msg += `Convidados: ${guests}\n`;
      if (eventDate) msg += `Data: ${formatDateBR(eventDate)}\n`;
      msg += `Local: ${locationToDefine ? 'A definir' : (eventLocation || '—')}\n`;
      if (selectedPackage) {
        msg += `\nPacote: ${selectedPackage.name}\n`;
        const items = activeItems.map((i) => i.label).join(', ');
        if (items) msg += `Itens inclusos: ${items}\n`;
        if (substitutePratoQuente) msg += `Substituição: Prato Quente → Almoço/Jantar\n`;
      }
      if (selectedDrinks.length > 0) {
        msg += `Bebidas: ${selectedDrinks.map((id) => drinkItems.find((d) => d.id === id)?.label).filter(Boolean).join(', ')}\n`;
      }
      msg += `Estrutura Buffet inclusa: R$${STRUCTURE_FEE_PER_PERSON}/pessoa\n`;
      if (selectedPartners.length > 0) {
        msg += `\nInteresse em parceiros: ${selectedPartners.map((id) => partners.find((p) => p.id === id)?.label).filter(Boolean).join(', ')}\n`;
      }
    } else {
      msg += `Tipo: Encomenda (Delivery)\n`;
      Object.entries(deliveryQuantities).forEach(([id, qty]) => {
        if (qty > 0) {
          const it = deliveryItems.find((d) => d.id === id);
          if (it) msg += `${it.label}: ${qty} ${it.unit}\n`;
        }
      });
    }
    msg += `\nValor estimado: R$${totalPrice.toLocaleString('pt-BR')}`;
    return msg;
  };

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildWhatsAppMessage())}`;

  const trackWhatsAppClick = () => {
    if (leadId) {
      axios.post(`${API}/leads/${leadId}/whatsapp-click`).catch(() => {});
    }
    trackEvent('whatsapp_clicked', { lead_id: leadId, total_price: totalPrice });
  };

  const downloadPDF = () => {
    trackEvent('pdf_downloaded', { lead_id: leadId, total_price: totalPrice });
    if (leadId) {
      axios.post(`${API}/leads/${leadId}/pdf-download`).catch(() => {});
    }
    generateBudgetPdf({
      leadName, leadWhatsapp,
      serviceType, eventType, guests, eventDate,
      eventLocation: locationToDefine ? 'A definir' : eventLocation,
      selectedPackage,
      activeItems,
      substitutePratoQuente,
      selectedDrinks, selectedPartners, deliveryQuantities,
      totalPrice,
    });
  };

  const resetSimulator = () => {
    setServiceType(null); setStep(0); setGuests(100);
    setEventType(null); setEventDate('');
    setEventLocation(''); setLocationToDefine(false);
    setSelectedPackageId(null); setExpandedPackageId(null);
    setExcludedItems(new Set()); setSubstitutePratoQuente(false);
    setSelectedDrinks([]); setSelectedPartners([]);
    setDeliveryQuantities({});
    setLeadName(''); setLeadWhatsapp(''); setLeadId(null);
    setTimeout(scrollToContent, 100);
  };

  // ---------- Service options ----------
  const serviceOptions = [
    { id: 'presencial', icon: PartyPopper, title: 'Evento Presencial', desc: 'Buffet completo com equipe e estrutura' },
    { id: 'delivery',   icon: Truck,       title: 'Encomenda',          desc: 'Salgados, bolos e doces para retirada/entrega' },
  ];

  return (
    <section ref={sectionRef} id="simulador" className="relative py-14 sm:py-20 md:py-28 bg-gradient-to-b from-[#0a0a0a] via-[#0d0a07] to-[#0a0a0a] bg-wine-glow">
      <div ref={contentRef} className="max-w-3xl mx-auto px-3 sm:px-6">
        {/* Heading */}
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <span className="font-body text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] uppercase text-[#C9A961]/80 mb-3 sm:mb-4 block">Simulador interativo</span>
          <h2 className="font-display text-[28px] sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4 leading-[1.1]">Monte seu evento</h2>
          <p className="font-body text-[13px] sm:text-base text-white/45 max-w-md mx-auto mb-4 sm:mb-5 px-2">Escolha um pacote pronto e personalize cada detalhe.</p>
          <div className="w-14 h-[1px] bg-gradient-to-r from-transparent via-[#C9A961]/60 to-transparent mx-auto" />
        </div>

        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="rounded-lg sm:rounded-xl border border-white/[0.06] bg-gradient-to-b from-white/[0.015] to-transparent p-4 sm:p-7 md:p-9 backdrop-blur-sm">
            {/* Progress */}
            <div className="mb-7 sm:mb-9">
              <div className="flex items-center justify-between mb-2.5">
                <span className="font-body text-[10px] sm:text-[11px] tracking-wider uppercase text-white/40">Etapa {step + 1} de {totalSteps}</span>
                <span className="font-body text-[11px] sm:text-xs font-medium text-[#C9A961] tracking-wide">{steps[step]}</span>
              </div>
              <div className="h-[3px] bg-white/[0.04] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#8B6F47] via-[#C9A961] to-[#D4B483] rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Floating Price */}
            {step > 0 && !isResultStep && !isLeadStep && totalPrice > 0 && (
              <div data-testid="floating-price" className="mb-7 sm:mb-9 px-4 sm:px-5 py-3 sm:py-4 rounded-md bg-gradient-to-r from-[#8B6F47]/20 via-[#8B6F47]/10 to-transparent border border-[#8B6F47]/30 flex items-center justify-between backdrop-blur-sm animate-fade-up">
                <div>
                  <span className="font-body text-[10px] tracking-[0.18em] uppercase text-white/40 block">Estimativa</span>
                  <span className="font-body text-[10px] text-white/30">para {guests} convidados</span>
                </div>
                <span key={totalPrice} className="text-transparent bg-clip-text bg-gradient-to-r from-[#E6CC8A] to-[#C9A961] font-display text-2xl sm:text-3xl md:text-4xl font-bold tabular-nums animate-fade-up">
                  R${totalPrice.toLocaleString('pt-BR')}
                </span>
              </div>
            )}

            {/* ============================================== */}
            {/* CONTENT PER STEP                                */}
            {/* ============================================== */}
            <div key={step} className={direction === 1 ? 'animate-step-in' : 'animate-step-in-reverse'}>

              {/* STEP 0 — Service Type */}
              {step === 0 && (
                <div>
                  <p className="text-center font-body text-[13px] sm:text-sm text-white/45 mb-5 sm:mb-6">Como você prefere?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {serviceOptions.map((opt) => (
                      <button key={opt.id} onClick={() => { setServiceType(opt.id); trackEvent('service_selected', { type: opt.id }); }}
                        data-testid={`service-option-${opt.id}`}
                        className={`item-lift relative p-6 sm:p-8 rounded-lg border text-center min-h-[160px] sm:min-h-[200px] ${
                          serviceType === opt.id ? 'border-[#C9A961] bg-gradient-to-br from-[#8B6F47]/15 to-transparent shadow-lg shadow-[#8B6F47]/15' : 'border-white/10 hover:border-[#8B6F47]/40 bg-white/[0.015]'
                        }`}>
                        {serviceType === opt.id && <Check className="absolute top-3 right-3 w-4 h-4 text-[#C9A961]" />}
                        <opt.icon className={`w-10 h-10 mx-auto mb-3 transition-colors ${serviceType === opt.id ? 'text-[#C9A961]' : 'text-white/30'}`} />
                        <span className="font-display text-lg sm:text-xl text-white block mb-1.5">{opt.title}</span>
                        <span className="font-body text-[12px] sm:text-xs text-white/45 leading-relaxed">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ===== DELIVERY FLOW (compact) ===== */}
              {serviceType === 'delivery' && step === 1 && (
                <div>
                  <p className="text-center font-body text-xs text-white/40 mb-5">Selecione os itens para encomenda.</p>
                  <div className="space-y-2.5">
                    {deliveryItems.map((it) => {
                      const qty = deliveryQuantities[it.id] || 0;
                      return (
                        <div key={it.id} className="p-4 rounded-sm border border-white/8 bg-white/[0.015] flex items-center justify-between">
                          <div>
                            <span className="font-body text-sm text-white block">{it.label}</span>
                            <span className="font-body text-[11px] text-white/40">{fmtBRL(it.pricePerUnit)} / {it.unit}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button onClick={() => updateDelivery(it.id, -1)} className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-white/55 hover:text-white hover:border-[#C9A961]/45 transition-all"><Minus className="w-3.5 h-3.5" /></button>
                            <span className="font-body text-sm text-white w-6 text-center tabular-nums">{qty}</span>
                            <button onClick={() => updateDelivery(it.id, 1)} data-testid={`delivery-add-${it.id}`} className="w-7 h-7 rounded-full border border-[#C9A961]/45 flex items-center justify-center text-[#C9A961] hover:bg-[#C9A961]/10 transition-all"><Plus className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ===== PRESENCIAL FLOW ===== */}

              {/* STEP 1 — Event data */}
              {serviceType === 'presencial' && step === 1 && (
                <div className="space-y-7 sm:space-y-9">
                  {/* Convidados — INPUT NUMÉRICO */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <label className="font-body text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/45">Convidados</label>
                      <span className="font-body text-[10px] text-[#C9A961]/80">obrigatório</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button type="button"
                        onClick={() => setGuests((g) => Math.max(40, g - 10))}
                        className="w-12 h-14 rounded-md border border-white/10 hover:border-[#C9A961]/40 active:scale-[0.97] text-white/60 hover:text-white transition-all flex items-center justify-center">
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min="40"
                        max="2000"
                        step="1"
                        inputMode="numeric"
                        value={guests}
                        onChange={(e) => {
                          const v = parseInt(e.target.value || '0', 10);
                          setGuests(Number.isNaN(v) ? 40 : v);
                        }}
                        onBlur={() => { if (guests < 40) setGuests(40); }}
                        data-testid="guests-input"
                        className="flex-1 px-3 sm:px-4 py-3 sm:py-3.5 rounded-md bg-white/[0.03] border border-white/12 text-white font-display text-2xl sm:text-3xl text-center tabular-nums focus:border-[#C9A961]/60 focus:bg-white/[0.05] focus:outline-none transition-all"
                      />
                      <button type="button"
                        onClick={() => setGuests((g) => g + 10)}
                        className="w-12 h-14 rounded-md border border-white/10 hover:border-[#C9A961]/40 active:scale-[0.97] text-white/60 hover:text-white transition-all flex items-center justify-center">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[11px] text-white/30 mt-2 text-center">Mínimo 40 convidados — digite o número exato</p>
                  </div>

                  {/* Tipo de evento */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <label className="font-body text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/45">Tipo de evento</label>
                      <span className="font-body text-[10px] text-[#C9A961]/80">obrigatório</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
                      {eventTypes.map((evt) => {
                        const Icon = evt.icon;
                        const active = eventType === evt.id;
                        return (
                          <button key={evt.id} onClick={() => { setEventType(evt.id); setSelectedPackageId(null); setExpandedPackageId(null); }}
                            data-testid={`event-type-${evt.id}`}
                            className={`item-lift py-3.5 sm:py-4 px-2 sm:px-3 rounded-md border text-center transition-all duration-200 min-h-[78px] sm:min-h-[88px] ${
                              active ? 'border-[#C9A961] bg-[#8B6F47]/12 text-white shadow-md shadow-[#8B6F47]/15' : 'border-white/10 text-white/60 hover:border-[#8B6F47]/30 hover:text-white/85'
                            }`}>
                            <Icon className={`w-5 h-5 mx-auto mb-1.5 ${active ? 'text-[#C9A961]' : 'text-white/35'}`} />
                            <span className="font-body text-[12px] sm:text-sm leading-tight block">{evt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Data — inline calendar */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <label className="font-body text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/45">Data do evento</label>
                      <span className="font-body text-[10px] text-[#C9A961]/80">obrigatório</span>
                    </div>
                    <InlineDatePicker
                      value={eventDate}
                      onChange={(d) => { setEventDate(d); trackEvent('date_picked', { date: d }); }}
                      blockedDates={blockedDates}
                    />
                    {eventDate && (
                      <p data-testid="event-date-pretty" className="mt-3 text-center font-body text-[13px] sm:text-sm text-white/65">
                        Data selecionada: <span className="text-[#C9A961] font-medium">{formatDateBR(eventDate)}</span>
                      </p>
                    )}
                  </div>

                  {/* Local */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <label className="font-body text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/45">Local do evento</label>
                      <span className="font-body text-[10px] text-[#C9A961]/80">obrigatório</span>
                    </div>
                    <input
                      type="text"
                      value={eventLocation}
                      onChange={(e) => setEventLocation(e.target.value)}
                      disabled={locationToDefine}
                      data-testid="event-location-input"
                      placeholder="Ex: Salão Vista Alegre, Belo Horizonte"
                      className="w-full px-4 py-3.5 rounded-md bg-white/[0.03] border border-white/12 text-white font-body text-sm focus:border-[#C9A961]/60 focus:bg-white/[0.05] focus:outline-none transition-all placeholder:text-white/20 disabled:opacity-40"
                    />
                    <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={locationToDefine}
                        onChange={(e) => setLocationToDefine(e.target.checked)}
                        data-testid="event-location-todefine"
                        className="w-4 h-4 accent-[#C9A961] cursor-pointer"
                      />
                      <span className="font-body text-[12px] sm:text-xs text-white/55 group-hover:text-white/75 transition-colors">
                        Ainda não tenho local definido — quero ajuda
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* STEP 2 — Choose Package (REDESIGN: clean card + inline expand) */}
              {serviceType === 'presencial' && step === 2 && (
                <div className="space-y-4 sm:space-y-5">
                  <div className="text-center mb-2 sm:mb-3">
                    <h3 className="font-display text-xl sm:text-2xl text-white mb-1.5">Escolha seu pacote</h3>
                    <p className="font-body text-xs sm:text-sm text-white/45 px-2">
                      Pacotes prontos para <span className="text-[#C9A961]">{eventTypes.find((e) => e.id === eventType)?.label}</span>. Personalize na próxima etapa.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {availablePackages.map((pkg) => {
                      const active = selectedPackageId === pkg.id;
                      const expanded = expandedPackageId === pkg.id;
                      const TierIcon = pkg.tier === 'premium' ? Crown : (pkg.tier === 'especial' ? Award : Sparkles);
                      const tierLabel = pkg.tier === 'premium' ? 'Premium' : (pkg.tier === 'especial' ? 'Especial' : 'Tradicional');
                      const accent = pkg.tier === 'premium' || pkg.tier === 'especial';
                      return (
                        <div
                          key={pkg.id}
                          data-testid={`package-card-${pkg.id}`}
                          className={`relative rounded-xl overflow-hidden border transition-all duration-300 ${
                            active
                              ? 'border-[#C9A961] shadow-[0_8px_30px_-12px_rgba(201,169,97,0.45)] bg-gradient-to-br from-[#8B6F47]/[0.10] via-transparent to-[#C9A961]/[0.04]'
                              : 'border-white/[0.08] hover:border-[#C9A961]/40 bg-white/[0.012]'
                          }`}
                        >
                          {/* Highlight stripe (top) */}
                          {pkg.highlight && (
                            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent" />
                          )}

                          <div className="p-4 sm:p-6">
                            {/* HEADER: tier badge + highlight tag */}
                            <div className="flex items-center justify-between mb-3 sm:mb-4">
                              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
                                accent
                                  ? 'border-[#C9A961]/40 bg-[#C9A961]/10 text-[#E6CC8A]'
                                  : 'border-white/15 bg-white/[0.04] text-white/55'
                              }`}>
                                <TierIcon className="w-3 h-3" />
                                <span className="font-body text-[10px] uppercase tracking-[0.18em] font-medium">{tierLabel}</span>
                              </div>
                              {pkg.highlight && (
                                <span className="text-[9px] uppercase tracking-[0.18em] font-semibold bg-gradient-to-r from-[#C9A961] to-[#E6CC8A] text-[#1a1611] px-2.5 py-1 rounded-full shadow-md">
                                  {pkg.highlight}
                                </span>
                              )}
                            </div>

                            {/* NAME */}
                            <h4 className="font-display text-lg sm:text-2xl text-white mb-1.5 leading-tight">{pkg.name}</h4>

                            {/* SUMMARY */}
                            <p className="font-body text-[12.5px] sm:text-sm text-white/50 mb-4 sm:mb-5 leading-relaxed">{pkg.summary}</p>

                            {/* PRICE — visually dominant */}
                            <div className="mb-4 sm:mb-5">
                              <div className="flex items-baseline gap-2">
                                <span className="font-body text-[11px] uppercase tracking-[0.18em] text-white/35">R$</span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E6CC8A] via-[#C9A961] to-[#D4B483] font-display text-4xl sm:text-5xl font-bold tabular-nums leading-none">
                                  {fmtBRL2(pkg.pricePerPerson)}
                                </span>
                                <span className="font-body text-[11px] text-white/35 ml-1">por pessoa</span>
                              </div>
                            </div>

                            {/* QUICK HIGHLIGHTS (3 selling-points with icons) */}
                            <div className="grid grid-cols-3 gap-2 mb-4 sm:mb-5">
                              <div className="flex flex-col items-center text-center py-2 px-1 rounded-md bg-white/[0.02] border border-white/[0.04]">
                                <UtensilsCrossed className="w-4 h-4 text-[#C9A961]/85 mb-1" />
                                <span className="font-body text-[10px] sm:text-[11px] text-white/55 leading-snug">{pkg.items.length} itens no menu</span>
                              </div>
                              <div className="flex flex-col items-center text-center py-2 px-1 rounded-md bg-white/[0.02] border border-white/[0.04]">
                                <Users className="w-4 h-4 text-[#C9A961]/85 mb-1" />
                                <span className="font-body text-[10px] sm:text-[11px] text-white/55 leading-snug">Equipe inclusa</span>
                              </div>
                              <div className="flex flex-col items-center text-center py-2 px-1 rounded-md bg-white/[0.02] border border-white/[0.04]">
                                <Shield className="w-4 h-4 text-[#C9A961]/85 mb-1" />
                                <span className="font-body text-[10px] sm:text-[11px] text-white/55 leading-snug">Estrutura inclusa</span>
                              </div>
                            </div>

                            {/* ACTIONS */}
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                onClick={() => setExpandedPackageId(expanded ? null : pkg.id)}
                                data-testid={`package-toggle-${pkg.id}`}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 sm:py-2.5 rounded-md border border-white/12 text-white/65 hover:text-white hover:border-[#C9A961]/40 font-body text-xs uppercase tracking-wider transition-all duration-200"
                              >
                                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                {expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedPackageId(pkg.id);
                                  setExcludedItems(new Set());
                                  setSubstitutePratoQuente(false);
                                  trackEvent('package_selected', { id: pkg.id });
                                }}
                                data-testid={`package-${pkg.id}`}
                                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 sm:py-2.5 rounded-md font-body text-xs uppercase tracking-wider font-semibold transition-all duration-300 ${
                                  active
                                    ? 'bg-[#C9A961] text-[#1a1611] shadow-md shadow-[#C9A961]/30'
                                    : 'bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] hover:shadow-lg hover:shadow-[#C9A961]/30 hover:-translate-y-0.5 active:scale-[0.98]'
                                }`}
                              >
                                {active ? <><Check className="w-3.5 h-3.5" /> Selecionado</> : <>Escolher <ArrowRight className="w-3.5 h-3.5" /></>}
                              </button>
                            </div>

                            {/* EXPANDABLE DETAILS */}
                            {expanded && (
                              <div className="mt-4 pt-4 border-t border-white/8 animate-fade-up">
                                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-[#C9A961]/85 block mb-2.5">O que está incluso</span>
                                <ul className="space-y-2">
                                  {pkg.items.map((it) => (
                                    <li key={it.id} className="flex items-start gap-2">
                                      <Check className="w-3.5 h-3.5 text-[#C9A961]/70 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <span className="font-body text-[13px] text-white/85 block">{it.label}</span>
                                        <span className="font-body text-[11px] text-white/40 block leading-snug">{it.desc}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                                <div className="mt-4 pt-3 border-t border-white/5">
                                  <span className="font-body text-[10px] uppercase tracking-[0.18em] text-[#C9A961]/85 block mb-2">Equipe inclusa</span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {pkg.staff.map((role) => (
                                      <span key={role} className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] font-body text-[10px] text-white/55">{role}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="font-body text-[11px] text-white/30 text-center mt-3 sm:mt-4 px-2">
                    + R${STRUCTURE_FEE_PER_PERSON}/pessoa de estrutura (taças, pratos, talheres, rechauds) já incluso no total
                  </p>
                </div>
              )}

              {/* STEP 3 — Personalize Package */}
              {serviceType === 'presencial' && step === 3 && selectedPackage && (
                <div className="space-y-5 sm:space-y-7">
                  <div className="text-center mb-2 sm:mb-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/25 mb-2">
                      <PackageIcon className="w-3.5 h-3.5 text-[#C9A961]" />
                      <span className="font-body text-[10px] uppercase tracking-[0.18em] text-[#E6CC8A]">{selectedPackage.name}</span>
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl text-white mb-1.5">Personalize seu pacote</h3>
                    <p className="font-body text-xs sm:text-sm text-white/45 px-2">Ajuste os itens conforme sua preferência (opcional).</p>
                  </div>

                  {/* Items */}
                  <div className="space-y-2 sm:space-y-2.5">
                    <span className="font-body text-[10px] tracking-[0.18em] uppercase text-white/45 block mb-1.5">Itens inclusos</span>
                    {selectedPackage.items.map((it) => {
                      const included = !excludedItems.has(it.id);
                      const isSwapTarget = it.swappable && substitutePratoQuente;
                      return (
                        <div
                          key={it.id}
                          className={`p-3 sm:p-3.5 rounded-md border transition-all duration-200 ${
                            isSwapTarget
                              ? 'border-[#C9A961]/25 bg-[#C9A961]/[0.03]'
                              : included
                                ? 'border-white/10 bg-white/[0.025] hover:border-white/15'
                                : 'border-white/5 bg-transparent opacity-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 sm:gap-3 min-w-0 flex-1">
                              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border mt-0.5 transition-all ${included && !isSwapTarget ? 'bg-[#C9A961] border-[#C9A961]' : 'border-white/20'}`}>
                                {included && !isSwapTarget && <Check className="w-3 h-3 text-[#0a0a0a]" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className={`font-body text-[13px] sm:text-sm block leading-snug ${isSwapTarget ? 'line-through text-white/35' : 'text-white'}`}>{it.label}</span>
                                <span className="font-body text-[11px] text-white/40 block leading-snug mt-0.5">{it.desc}</span>
                                {isSwapTarget && (
                                  <span className="text-[10px] text-[#C9A961] uppercase tracking-wider mt-1.5 inline-block">
                                    Substituído por Almoço/Jantar
                                  </span>
                                )}
                              </div>
                            </div>
                            {!isSwapTarget && (
                              <button
                                type="button"
                                onClick={() => toggleExcluded(it.id)}
                                data-testid={`toggle-item-${it.id}`}
                                className="font-body text-[10px] tracking-wider uppercase text-white/35 hover:text-[#C9A961] active:scale-95 transition-all flex-shrink-0 px-2 py-1 rounded border border-transparent hover:border-white/10"
                              >
                                {included ? 'Remover' : 'Restaurar'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Substitution Prato Quente -> Almoço/Jantar */}
                  {hasPratoQuente && (
                    <div className={`p-4 sm:p-5 rounded-lg border transition-all duration-300 ${substitutePratoQuente ? 'border-[#C9A961]/50 bg-gradient-to-br from-[#C9A961]/[0.10] to-transparent shadow-md shadow-[#C9A961]/10' : 'border-white/12 bg-white/[0.02] hover:border-[#C9A961]/30'}`}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={substitutePratoQuente}
                          onChange={(e) => setSubstitutePratoQuente(e.target.checked)}
                          data-testid="substitute-prato-quente"
                          className="w-5 h-5 accent-[#C9A961] mt-0.5 cursor-pointer flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Repeat className="w-3.5 h-3.5 text-[#C9A961]" />
                            <span className="font-body text-[13.5px] sm:text-sm text-white font-semibold">Substituir Prato Quente por Almoço/Jantar</span>
                          </div>
                          <span className="font-body text-[11.5px] sm:text-xs text-white/55 block leading-relaxed">{ALMOCO_JANTAR.desc}</span>
                          <span className="font-body text-[11.5px] sm:text-xs text-[#C9A961]/90 mt-2 block font-medium">
                            + R$ {fmtBRL2(ALMOCO_JANTAR.pricePerPerson)} por pessoa
                          </span>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4 — Drinks */}
              {serviceType === 'presencial' && step === 4 && (
                <div>
                  <div className="text-center mb-4 sm:mb-5">
                    <h3 className="font-display text-xl sm:text-2xl text-white mb-1.5">Bebidas</h3>
                    <p className="font-body text-xs sm:text-sm text-white/45 px-2">Opcional. Preço por pessoa.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5">
                    {drinkItems.map((item) => {
                      const sel = selectedDrinks.includes(item.id);
                      return (
                        <button key={item.id} onClick={() => toggleDrink(item.id)}
                          data-testid={`drink-item-${item.id}`}
                          className={`item-lift p-3.5 sm:p-4 rounded-md border flex items-center justify-between text-left transition-all ${
                            sel ? 'border-[#C9A961] bg-[#8B6F47]/10' : 'border-white/8 hover:border-[#8B6F47]/30 bg-white/[0.015]'
                          }`}>
                          <div className="flex items-start gap-3 min-w-0 pr-3 flex-1">
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border mt-0.5 ${sel ? 'bg-[#C9A961] border-[#C9A961]' : 'border-white/20'}`}>
                              {sel && <Check className="w-3 h-3 text-[#0a0a0a]" />}
                            </div>
                            <div className="min-w-0">
                              <span className="font-body text-[13.5px] sm:text-sm text-white block font-medium">{item.label}</span>
                              <span className="font-body text-[11px] text-white/40 block leading-snug mt-0.5">{item.description}</span>
                            </div>
                          </div>
                          <span className="text-[#D4B483] font-body text-sm font-semibold flex-shrink-0 whitespace-nowrap">R${item.pricePerPerson}<span className="text-white/30 text-[10px] font-normal">/p</span></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 5 — Equipe & Estrutura (informativo) */}
              {serviceType === 'presencial' && step === 5 && selectedPackage && (
                <div className="space-y-6">
                  <div className="text-center mb-2">
                    <h3 className="font-display text-xl text-white mb-1">O que já está incluso</h3>
                    <p className="font-body text-xs text-white/40">Sem custos extras — equipe e estrutura inclusas no pacote.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Equipe */}
                    <div className="p-4 rounded-md border border-white/10 bg-white/[0.015]">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-4 h-4 text-[#C9A961]" />
                        <span className="font-body text-[10px] uppercase tracking-[0.18em] text-[#C9A961]">Equipe inclusa</span>
                      </div>
                      <ul className="space-y-1.5">
                        {selectedPackage.staff.map((role) => (
                          <li key={role} className="flex items-start gap-2">
                            <Check className="w-3 h-3 text-[#C9A961]/70 mt-1 flex-shrink-0" />
                            <span className="font-body text-xs text-white/70">{role}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Estrutura */}
                    <div className="p-4 rounded-md border border-white/10 bg-white/[0.015]">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-[#C9A961]" />
                        <span className="font-body text-[10px] uppercase tracking-[0.18em] text-[#C9A961]">Estrutura inclusa</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {structureItems.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/8 font-body text-[11px] text-white/55">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Partners */}
                  <div>
                    <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-2">Parceiros (opcional — só interesse)</span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {partners.map((p) => {
                        const sel = selectedPartners.includes(p.id);
                        return (
                          <button key={p.id} onClick={() => togglePartner(p.id)}
                            data-testid={`partner-${p.id}`}
                            className={`item-lift py-2.5 px-3 rounded-sm border text-center text-xs transition-all ${
                              sel ? 'border-[#C9A961] bg-[#8B6F47]/10 text-white' : 'border-white/8 text-white/55 hover:border-[#8B6F47]/30'
                            }`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* LEAD STEP */}
              {isLeadStep && (
                <div className="max-w-md mx-auto">
                  <div className="text-center mb-6">
                    <h3 className="font-display text-xl text-white mb-1">Receba seu orçamento</h3>
                    <p className="font-body text-xs text-white/40">Preencha para gerar a proposta e falar com nosso especialista.</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="font-body text-[10px] tracking-[0.15em] uppercase text-white/35 block mb-1.5">Seu nome</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <input
                          type="text"
                          value={leadName}
                          onChange={(e) => setLeadName(e.target.value)}
                          data-testid="lead-name-input"
                          placeholder="Como você se chama?"
                          className="w-full pl-10 pr-3 py-3 rounded-sm bg-white/[0.03] border border-white/10 text-white font-body text-sm focus:border-[#C9A961]/50 focus:outline-none transition-colors placeholder:text-white/20"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="font-body text-[10px] tracking-[0.15em] uppercase text-white/35 block mb-1.5">WhatsApp</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <input
                          type="tel"
                          value={leadWhatsapp}
                          onChange={(e) => setLeadWhatsapp(e.target.value)}
                          data-testid="lead-whatsapp-input"
                          placeholder="(31) 9 0000-0000"
                          className="w-full pl-10 pr-3 py-3 rounded-sm bg-white/[0.03] border border-white/10 text-white font-body text-sm focus:border-[#C9A961]/50 focus:outline-none transition-colors placeholder:text-white/20"
                        />
                      </div>
                    </div>
                    <p className="font-body text-[10px] text-white/30 text-center pt-2">
                      Seus dados são confidenciais. Nunca compartilhamos com terceiros.
                    </p>
                  </div>
                </div>
              )}

              {/* RESULT STEP */}
              {isResultStep && (
                <div className="text-center max-w-2xl mx-auto">
                  <div className="mb-6 sm:mb-8 inline-flex p-3 rounded-full bg-gradient-to-br from-[#8B6F47]/20 to-[#C9A961]/15 border border-[#C9A961]/25">
                    <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-[#C9A961]" />
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-white mb-2 leading-tight">Sua proposta personalizada</h3>
                  <p className="font-body text-[13px] sm:text-sm text-white/45 mb-6 sm:mb-8 px-2">Tudo pronto, {leadName.split(' ')[0]}. Aqui está o resumo do seu evento.</p>

                  <div className="text-left mb-6 sm:mb-8 p-4 sm:p-6 rounded-lg border border-white/8 bg-white/[0.015]">
                    {serviceType === 'presencial' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 pb-4 border-b border-white/8">
                          <div>
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block">Evento</span>
                            <span className="font-body text-[13px] sm:text-sm text-white">{eventTypes.find((e) => e.id === eventType)?.label}</span>
                          </div>
                          <div>
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block">Convidados</span>
                            <span className="font-body text-[13px] sm:text-sm text-white">{guests}</span>
                          </div>
                          {eventDate && (
                            <div>
                              <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block">Data</span>
                              <span className="font-body text-[13px] sm:text-sm text-white">{formatDateBR(eventDate)}</span>
                            </div>
                          )}
                          <div className="col-span-2">
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block">Local</span>
                            <span className="font-body text-[13px] sm:text-sm text-white">{locationToDefine ? 'A definir' : (eventLocation || '—')}</span>
                          </div>
                        </div>
                        {selectedPackage && (
                          <div className="mb-4 pb-4 border-b border-white/8">
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block mb-2">Pacote escolhido</span>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className="font-display text-base text-white">{selectedPackage.name}</span>
                              <span className="font-body text-[#D4B483] text-sm font-semibold whitespace-nowrap">R$ {fmtBRL2(selectedPackage.pricePerPerson)}/p</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {activeItems.map((it) => (
                                <span key={it.id} className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/8 font-body text-[11px] text-white/60">{it.label}</span>
                              ))}
                              {substitutePratoQuente && (
                                <span className="px-2.5 py-1 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/35 font-body text-[11px] text-[#E6CC8A]">+ Almoço/Jantar</span>
                              )}
                            </div>
                          </div>
                        )}
                        {selectedDrinks.length > 0 && (
                          <div className="mb-4 pb-4 border-b border-white/8">
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block mb-2">Bebidas</span>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedDrinks.map((id) => {
                                const item = drinkItems.find((d) => d.id === id);
                                return item ? (
                                  <span key={id} className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/8 font-body text-[11px] text-white/65">
                                    {item.label} <span className="text-[#D4B483]/80">· R${item.pricePerPerson}/p</span>
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                        <div className="mb-4 pb-4 border-b border-white/8 flex items-center justify-between gap-2">
                          <div>
                            <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block">Estrutura Buffet</span>
                            <span className="font-body text-[11px] text-white/45">Inclusa · taças, pratos, talheres, rechauds, fogão e mais</span>
                          </div>
                          <span className="font-body text-[11px] text-white/60 whitespace-nowrap">R${STRUCTURE_FEE_PER_PERSON}/p</span>
                        </div>
                      </>
                    ) : (
                      <div className="mb-4 pb-4 border-b border-white/8">
                        <span className="font-body text-[10px] text-white/30 uppercase tracking-wider block mb-2">Itens da encomenda</span>
                        {Object.entries(deliveryQuantities).map(([id, qty]) => {
                          if (qty <= 0) return null;
                          const item = deliveryItems.find((d) => d.id === id);
                          return item ? (
                            <div key={id} className="flex justify-between font-body text-sm text-white/70 py-1">
                              <span>{item.label}</span><span className="text-[#D4B483]">{qty} {item.unit}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 gap-3">
                      <span className="font-body text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/35">Valor total estimado</span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E6CC8A] to-[#C9A961] font-display text-2xl sm:text-3xl font-bold tabular-nums">
                        R${totalPrice.toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:justify-center sm:gap-3">
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={trackWhatsAppClick}
                      data-testid="whatsapp-cta-final"
                      className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body font-semibold text-[13px] sm:text-sm tracking-[0.08em] uppercase px-8 sm:px-10 py-4 rounded-md hover:shadow-xl hover:shadow-[#C9A961]/30 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300">
                      <MessageCircle className="w-4 h-4" />
                      Falar com especialista agora
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <button
                      onClick={downloadPDF}
                      data-testid="download-pdf-btn"
                      className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/8 hover:border-[#C9A961]/60 font-body font-medium text-[11.5px] sm:text-xs tracking-[0.12em] uppercase px-7 sm:px-8 py-3.5 sm:py-3 rounded-md transition-all duration-300"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Baixar orçamento (PDF)
                    </button>
                    <button onClick={resetSimulator} data-testid="reset-simulator-btn" className="block mx-auto pt-2 font-body text-xs text-white/30 hover:text-[#C9A961] transition-colors">
                      Refazer simulação
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            {!isResultStep && (
              <div className="flex justify-between items-center mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-white/5 gap-3">
                <button onClick={goPrev} disabled={step === 0}
                  data-testid="step-back-btn"
                  className="flex items-center gap-1.5 font-body text-[13px] sm:text-sm text-white/45 hover:text-white px-3 py-2.5 transition-colors disabled:opacity-0 disabled:pointer-events-none">
                  <ChevronLeft className="w-4 h-4" /> Voltar
                </button>
                <button onClick={goNext} disabled={!canProceed()}
                  data-testid="step-next-btn"
                  className="group flex items-center gap-2 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-[12.5px] sm:text-sm font-semibold tracking-[0.08em] uppercase px-6 sm:px-8 py-3.5 sm:py-3 rounded-md disabled:opacity-30 disabled:cursor-not-allowed enabled:hover:shadow-lg enabled:hover:shadow-[#C9A961]/25 enabled:hover:-translate-y-0.5 enabled:active:scale-[0.98] transition-all duration-300 min-h-[48px]">
                  Próximo <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventSimulator;
